import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TablePagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getSignaturesUi } from "../../../api/ui";

interface SignatureRowItem {
  id: string;
  title?: string;
  authorName?: string;
  authorEmail?: string;
  hash?: string;
  documentHash?: string;
  status?: string;
  createdAt?: string;
}

interface SignaturePageResponse {
  content?: SignatureRowItem[];
  totalElements?: number;
}

export function AssinaturasList(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [signatures, setSignatures] = useState<SignatureRowItem[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination State initialized from URL query parameters
  const [page, setPage] = useState<number>(() => {
    const p = parseInt(searchParams.get("page") || "0", 10);
    return isNaN(p) || p < 0 ? 0 : p;
  });
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const s = parseInt(searchParams.get("size") || "10", 10);
    return isNaN(s) || s <= 0 ? 10 : s;
  });
  // Search State: searchInput for typed text, appliedQuery for executed filter
  const [searchInput, setSearchInput] = useState<string>(() => searchParams.get("q") || "");
  const [appliedQuery, setAppliedQuery] = useState<string>(() => searchParams.get("q") || "");

  // Copy Hash State
  const [copiedHashId, setCopiedHashId] = useState<string | null>(null);

  const updateQueryParams = (newParams: { page?: number; size?: number; q?: string }): void => {
    const targetPage = newParams.page !== undefined ? newParams.page : page;
    const targetSize = newParams.size !== undefined ? newParams.size : rowsPerPage;
    const targetQ = newParams.q !== undefined ? newParams.q : appliedQuery;

    const params: Record<string, string> = {};
    if (targetPage > 0) params.page = String(targetPage);
    if (targetSize !== 10) params.size = String(targetSize);
    if (targetQ.trim()) params.q = targetQ.trim();

    setSearchParams(params, { replace: true });
  };

  const handleCopyHash = (hash: string, id: string): void => {
    if (!hash) return;
    void navigator.clipboard.writeText(hash);
    setCopiedHashId(id);
    setTimeout(() => { setCopiedHashId(null); }, 2000);
  };

  const loadData = useCallback(async (
    currentPage = page,
    currentRowsPerPage = rowsPerPage,
    currentQuery = appliedQuery
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const responseData = (await getSignaturesUi(currentPage, currentRowsPerPage, currentQuery)) as SignaturePageResponse | SignatureRowItem[];
      if (typeof responseData === "object" && "content" in responseData) {
        setSignatures(responseData.content || []);
        setTotalElements(responseData.totalElements ?? (responseData.content?.length || 0));
      } else if (Array.isArray(responseData)) {
        setSignatures(responseData);
        setTotalElements(responseData.length);
      } else {
        setSignatures([]);
        setTotalElements(0);
      }
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } } };
      const msg = errorObj.response?.data?.message;
      setError(typeof msg === "string" ? msg : t("assinaturas.list.loadError"));
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, page, rowsPerPage, t]);

  useEffect(() => {
    void loadData(page, rowsPerPage, appliedQuery);
  }, [loadData, page, rowsPerPage, appliedQuery]);

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage);
    updateQueryParams({ page: newPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    updateQueryParams({ page: 0, size: newRowsPerPage });
  };

  const handleExecuteSearch = (): void => {
    setAppliedQuery(searchInput);
    setPage(0);
    updateQueryParams({ page: 0, q: searchInput });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleExecuteSearch();
    }
  };

  const handleClearSearch = (): void => {
    setSearchInput("");
    setAppliedQuery("");
    setPage(0);
    updateQueryParams({ page: 0, q: "" });
  };

  const getStatusChip = (status?: string): React.ReactElement => {
    const upper = (status || "").toUpperCase();
    switch (upper) {
      case "COMPLETED":
      case "FINISHED":
      case "CONCLUIDO":
      case "CONCLUÍDO":
      case "SIGNED":
      case "ASSINADO":
        return <Chip color="success" label={t("assinaturas.list.statusCompleted")} size="small" />;
      case "PENDING":
      case "PENDENTE":
        return <Chip color="warning" label={t("assinaturas.list.statusPending")} size="small" />;
      case "EXPIRED":
      case "EXPIRADO":
        return <Chip color="error" label={t("assinaturas.list.statusExpired")} size="small" />;
      default:
        return <Chip color="default" label={status || "N/A"} size="small" />;
    }
  };

  return (
    <Box>
      {/* Top Header Row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography color="#1e293b" component="h1" fontWeight="bold" variant="h4">
          {t("assinaturas.list.title")}
        </Typography>
        <Button
          onClick={() => { navigate("/assinaturas/create"); }}
          startIcon={<AddIcon />}
          sx={{ bgcolor: "#2F16C9", fontWeight: "bold", height: 40, "&:hover": { bgcolor: "#0C1C69" } }}
          variant="contained"
        >
          {t("assinaturas.list.newSignature")}
        </Button>
      </Box>

      {/* Search and Action Toolbar Row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexGrow: 1, maxWidth: 600 }}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            fullWidth
            onChange={(e) => { setSearchInput(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder={t("assinaturas.list.searchPlaceholder")}
            size="small"
            sx={{ bgcolor: "#fff", borderRadius: 1 }}
            value={searchInput}
          />
          <Button
            onClick={handleExecuteSearch}
            size="medium"
            startIcon={<SearchIcon />}
            sx={{
              borderColor: "#2F16C9",
              color: "#2F16C9",
              fontWeight: "bold",
              textTransform: "none",
              height: 40,
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "#0C1C69", color: "#0C1C69" },
            }}
            variant="outlined"
          >
            {t("assinaturas.list.searchLabel")}
          </Button>
        </Box>

        <IconButton
          onClick={() => { void loadData(page, rowsPerPage, appliedQuery); }}
          sx={{ bgcolor: "#fff", border: "1px solid #cbd5e1", borderRadius: 1, width: 40, height: 40 }}
          title={t("assinaturas.list.refresh")}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.list.columnTitle")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.list.columnAuthor")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.list.columnHash")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.list.columnStatus")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("assinaturas.list.columnCreated")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {t("assinaturas.list.columnActions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {signatures.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 4, color: "text.secondary" }}>
                      {t("assinaturas.list.noSignatures")}
                    </TableCell>
                  </TableRow>
                ) : (
                  signatures.map((row) => {
                    const hashValue = row.hash || row.documentHash || row.id || "";
                    const isCopied = copiedHashId === row.id;

                    return (
                      <TableRow hover key={row.id}>
                        <TableCell sx={{ fontWeight: 500 }}>{row.title || row.id}</TableCell>
                        <TableCell>{row.authorName || row.authorEmail || "-"}</TableCell>
                        <TableCell>
                          {hashValue ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, maxWidth: 160 }}>
                              <Tooltip
                                title={
                                  isCopied
                                    ? t("assinaturas.list.hashCopied")
                                    : hashValue
                                }
                              >
                                <Typography
                                  onClick={() => { handleCopyHash(hashValue, row.id); }}
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.82rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                    color: "text.secondary",
                                    "&:hover": { color: "#2F16C9", textDecoration: "underline" },
                                  }}
                                  variant="body2"
                                >
                                  {hashValue}
                                </Typography>
                              </Tooltip>
                              <Tooltip title={isCopied ? t("assinaturas.list.hashCopied") : t("assinaturas.list.copyHash")}>
                                <IconButton
                                  onClick={() => { handleCopyHash(hashValue, row.id); }}
                                  size="small"
                                  sx={{ p: 0.5, color: isCopied ? "success.main" : "action.active" }}
                                >
                                  {isCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{getStatusChip(row.status)}</TableCell>
                        <TableCell>
                          {row.createdAt ? (
                            <Tooltip title={new Date(row.createdAt).toLocaleString()}>
                              <Typography component="span" sx={{ cursor: "pointer" }} variant="body2">
                                {new Date(row.createdAt).toLocaleDateString()}
                              </Typography>
                            </Tooltip>
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Tooltip title={t("assinaturas.list.viewDetails")}>
                            <IconButton
                              color="primary"
                              onClick={() => { navigate(`/assinaturas/${row.id}`); }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            showFirstButton
            showLastButton
          />
        </Paper>
      )}
    </Box>
  );
};

export default AssinaturasList;
